import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useApi } from '../../utils/hooks';
import '../../utils/styles/FullScreenPopup.css';

/**
 * FullScreenPopup
 * Displays a full-screen popup for a specified duration.
 * Once closed, the popup will not reappear until the specified duration has passed.
 *
 * @param {number} props.duration - Time (in milliseconds) to keep the popup hidden after it has been closed.
 * @param {function} props.onClose - Callback function triggered when the popup is closed.
 * @returns {JSX.Element|null} - The rendered popup or null if not visible.
 */
function FullScreenPopup({ duration, onClose }) {
    const [isVisible, setIsVisible] = useState(false);

    // Fetching popup data using the custom hook
    const { data, loading, error } = useApi('popup/');

    // Retrieve popup visibility based on the last close time stored in localStorage
    useEffect(() => {
        const closedTime = localStorage.getItem('popupClosedTime');
        const currentTime = Date.now();

        if (!closedTime || currentTime - closedTime > duration) {
            setIsVisible(true); // Show the popup if time elapsed is greater than duration
        } else {
            onClose(); // Call onClose to trigger showing the main content
        }
    }, [duration, onClose]);

    // Disable scrolling when the popup is visible
    useEffect(() => {
        if (isVisible && !error && !loading && data?.results?.length) {
            document.body.style.overflow = 'hidden'; // Disable scrolling when the popup is visible
        } else {
            document.body.style.overflow = ''; // Enable scrolling when the popup is hidden
        }

        return () => {
            document.body.style.overflow = ''; // Enable scrolling when the component is unmounted
        };
    }, [isVisible, error, loading, data?.results]);

    // Handle closing of the popup and storing close time
    const handleClose = () => {
        localStorage.setItem('popupClosedTime', Date.now());
        setIsVisible(false);
        onClose();
    };

    // Skip rendering loading or error UI (as per the requirement)
    if (loading || error || !isVisible || !data?.results?.length) {
        return null; // Do not render anything if in loading or error state
    }

    const popupData = data.results[0];

    return (
        <div id="popup">
            <img
                src={popupData.img}
                alt="Popup visuel"
            />
            <div id="popup-content">
                <p className="title-2">{popupData.title}</p>
                <p id="popup-info">{popupData.content}</p>
                <button onClick={handleClose}>Compris</button>
            </div>
        </div>
    );
}

FullScreenPopup.propTypes = {
    duration: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default FullScreenPopup;
